import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Calendar, TrendingUp, Users, Plus, BarChart3 } from "lucide-react";
import { Event } from "@/types/event";
import { useNavigate } from "react-router-dom";

interface DashboardQuickStatsProps {
  createdEvents: Event[];
  invitedEvents: Event[];
  onCreateEvent: () => void;
  userEmail?: string;
}

export const DashboardQuickStats = ({ 
  createdEvents, 
  invitedEvents,
  onCreateEvent,
  userEmail
}: DashboardQuickStatsProps) => {
  const navigate = useNavigate();

  // Calculate stats
  const now = new Date();
  const firstDayOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
  
  const eventsThisMonth = createdEvents.filter(event => 
    new Date(event.rsvpDate || event.deadline || '') >= firstDayOfMonth
  ).length;

  const activeEvents = createdEvents.filter(event => 
    event.isActive && (!event.deadline || new Date(event.deadline) >= now)
  ).length;

  const pendingVotes = invitedEvents.filter(event => {
    if (!event.isActive || !userEmail) return false;
    const currentGuest = event.guests.find(g => g.email === userEmail);
    return !currentGuest?.hasVoted;
  }).length;

  const totalGuests = createdEvents.reduce((sum, event) => sum + event.guests.length, 0);

  const stats = [
    {
      title: "Events This Month",
      value: eventsThisMonth,
      icon: Calendar,
      color: "text-blue-600 dark:text-blue-400",
      bgColor: "bg-blue-50 dark:bg-blue-950/50",
    },
    {
      title: "Active Events",
      value: activeEvents,
      icon: TrendingUp,
      color: "text-green-600 dark:text-green-400",
      bgColor: "bg-green-50 dark:bg-green-950/50",
    },
    {
      title: "Pending Votes",
      value: pendingVotes,
      icon: BarChart3,
      color: "text-orange-600 dark:text-orange-400",
      bgColor: "bg-orange-50 dark:bg-orange-950/50",
    },
    {
      title: "Total Guests",
      value: totalGuests,
      icon: Users,
      color: "text-purple-600 dark:text-purple-400",
      bgColor: "bg-purple-50 dark:bg-purple-950/50",
    },
  ];

  return (
    <Card className="mb-6 overflow-hidden border-2 shadow-sm">
      <CardHeader className="bg-gradient-to-r from-primary/5 via-primary/10 to-secondary/5 pb-3">
        <CardTitle className="text-lg font-semibold flex items-center gap-2">
          <TrendingUp className="w-5 h-5" />
          Quick Stats
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-6">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {stats.map((stat) => (
            <div
              key={stat.title}
              className={`${stat.bgColor} rounded-xl p-4 transition-all hover:scale-105 cursor-pointer`}
            >
              <div className="flex items-center justify-between mb-2">
                <stat.icon className={`w-5 h-5 ${stat.color}`} />
              </div>
              <div className={`text-2xl font-bold ${stat.color}`}>
                {stat.value}
              </div>
              <div className="text-xs text-muted-foreground mt-1">
                {stat.title}
              </div>
            </div>
          ))}
        </div>

        {pendingVotes > 0 && (
          <div className="mt-4 p-3 bg-orange-50 dark:bg-orange-950/50 rounded-lg border border-orange-200 dark:border-orange-800">
            <p className="text-sm text-orange-800 dark:text-orange-200 font-medium">
              ⏰ You have {pendingVotes} pending {pendingVotes === 1 ? 'vote' : 'votes'} waiting for your response!
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
